import moment from 'moment';
import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Modal } from 'react-bootstrap';
import Tooltip from '@mui/material/Tooltip';
import ApiService from '../../api/ApiService';
import notification from '../../helpers/notification';
import { capitalize } from '../../utils/util';

const NityasevaTableRow = ({ nityaseva, onStatusUpdate }) => {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeSubscriptionId, setActiveSubscriptionId] = useState(null);

  const { subscription } = nityaseva;
  const { id, frequency, start_date, end_date, status, sub_id, cancellation_reason, schemaname } = subscription || {};

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      cancelReason: '',
      otherReason: '',
    },
  });

  const selectedReason = watch('cancelReason');

  const openCancelModal = useCallback((subId) => {
    setActiveSubscriptionId(subId);
    setIsCancelModalOpen(true);
  }, []);

  const closeCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
    reset();
  }, [reset]);

  const openViewModal = useCallback(() => {
    setIsViewModalOpen(true);
  }, []);

  const closeViewModal = useCallback(() => {
    setIsViewModalOpen(false);
  }, []);

  const handleNityaSevaCancel = async (data) => {
    try {
      setIsLoading(true);
      const payload = {
        cancelReason: data.cancelReason,
        sub_id: activeSubscriptionId,
      };

      await ApiService.nityaSevaCancel(payload);

      notification('success', 'Nitya Seva cancelled successfully');
      onStatusUpdate();
      closeCancelModal();
    } catch (error) {
      notification('error', 'Failed to cancel Nitya Seva');
      console.error('Error cancelling Nitya Seva:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNityaSevaPause = async (type) => {
    const action = type === 'hold' ? 'pause' : 'resume';
    const confirmAction = window.confirm(`Are you sure you want to ${action} your subscription?`);
    if (!confirmAction) return;

    try {
      setIsLoading(true);
      const payload = { type, sub_id };

      const response = await ApiService.nityaSevaPause(payload);

      if (response?.status === 'paused') {
        notification('success', 'Your subscription has been paused');
      }
      if (response?.status === 'active') {
        notification('success', 'Your subscription has been resumed');
      }
      onStatusUpdate();
      closeViewModal();
    } catch (error) {
      notification('error', 'Failed to update subscription status');
      console.error('Error updating subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isActive = status === 'active';
  const isPaused = status === 'paused';
  const isCancelled = status === 'cancelled';

  return (
    <div className="row align-items-center py-3">
      {/* ... (render the table row content) */}
      <div className="col-lg-2">
        <div className="form-field">
          <h5 className="form-title">Date :</h5>
          <p id="donation-date">{moment(nityaseva?.donation_date).format('DD/MM/YYYY')}</p>
        </div>
      </div>

      <div className="col-lg-2">
        <div className="form-field">
          <h5 className="form-title">Purpose :</h5>
          <p id="donation-date">{nityaseva?.scheme_type?.name}</p>
        </div>
      </div>

      <div className="col-lg-2">
        <div className="form-field">
          <h5 className="form-title">Status :</h5>
          <p
            id="donation-status"
            className={`text-uppercase ${isActive ? 'text-success' : isPaused ? 'text-warning' : 'text-danger'}`}
          >
            {capitalize(status)}
          </p>
        </div>
      </div>

      <div className="col-lg-2">
        <div className="form-field">
          <h5 className="form-title">Name :</h5>
          <p id="donation-name">{nityaseva?.donor_name}</p>
        </div>
      </div>

      <div className="col-lg-2">
        <div className="form-field">
          <h5 className="form-title">Amount :</h5>
          <p id="donation-amount">₹{nityaseva?.amount}</p>
        </div>
      </div>

      <div className="col-lg-2">
        <div className="form-field">
          <h5 className="form-title">Action :</h5>
          <div className="d-flex gap-2">
            <Tooltip title="View details" placement="bottom-start">
              <button onClick={openViewModal} className="btn btn-outline-dark btn-sm" disabled={isLoading}>
                VIEW
              </button>
            </Tooltip>

            {!isCancelled && (
              <Tooltip title="Cancel Nitya Seva Donation" placement="bottom-start">
                <button
                  onClick={() => openCancelModal(sub_id)}
                  className="btn btn-outline-danger btn-sm"
                  disabled={isLoading}
                >
                  CANCEL
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Modal */}
      <Modal centered show={isCancelModalOpen} onHide={closeCancelModal}>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Nitya Seva</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit(handleNityaSevaCancel)}>
            <div className="row">
              <div className="col-12 mb-3">
                <label htmlFor="cancelReason" className="form-label">
                  We truly respect your decision. If you're comfortable sharing, may we kindly know the reason for the cancellation? It would help us improve and serve you better in the future. <span className="text-danger">*</span>
                </label>
              </div>
              <div className="col-12">
                <select
                  className="form-control"
                  id="cancelReason"
                  {...register('cancelReason', {
                    required: 'Cancellation reason is required',
                  })}
                >
                  <option value="">Select a reason...</option>
                  <option value="Switched to manual donations">Switched to manual donations</option>
                  <option value="Temporary pause">Temporary pause</option>
                  <option value="Completed My Intended Contribution Period">Completed My Intended Contribution Period</option>
                  <option value="Technical Issues with the Auto-Debit System">Technical Issues with the Auto-Debit System</option>
                  <option value="Lack of Communication or Updates">Lack of Communication or Updates</option>
                  <option value="Unaware of Auto-Debit Enrollment">Unaware of Auto-Debit Enrollment</option>
                  <option value="Financial Constraints">Financial Constraints</option>
                  <option value="Others (Specify)">Others</option>
                </select>

                {errors.cancelReason && <div className="text-danger mt-1">{errors.cancelReason.message}</div>}

                {selectedReason === 'Others (Specify)' && (
                  <input
                    type="text"
                    className="form-control mt-2"
                    placeholder="Please specify your reason"
                    {...register('otherReason', {
                      required: 'Please specify your reason',
                      minLength: {
                        value: 5,
                        message: 'Please enter at least 5 characters',
                      },
                    })}
                  />
                )}

                {selectedReason === 'Others (Specify)' && errors.otherReason && (
                  <div className="text-danger mt-1">{errors.otherReason.message}</div>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeCancelModal}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-danger" disabled={isLoading}>
                {isLoading ? 'Submitting...' : 'Submit Cancellation'}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* View Details Modal */}
      <Modal centered show={isViewModalOpen} onHide={closeViewModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Nitya Seva Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="container-fluid">
            <div className="row mb-3">
              <div className="col-4">
                <strong>Subscription ID:</strong>
              </div>
              <div className="col-8">{sub_id}</div>
            </div>

            <div className="row mb-3">
              <div className="col-4">
                <strong>Donation Name:</strong>
              </div>
              <div className="col-8">{schemaname}</div>
            </div>

            <div className="row mb-3">
              <div className="col-4">
                <strong>Frequency:</strong>
              </div>
              <div className="col-8">{frequency}</div>
            </div>

            <div className="row mb-3">
              <div className="col-4">
                <strong>Start Date:</strong>
              </div>
              <div className="col-8">{moment(start_date).format('DD/MM/YYYY')}</div>
            </div>

            <div className="row mb-3">
              <div className="col-4">
                <strong>End Date:</strong>
              </div>
              <div className="col-8">{end_date ? moment(end_date).format('DD/MM/YYYY') : 'N/A'}</div>
            </div>

            <div className="row mb-3">
              <div className="col-4">
                <strong>Status:</strong>
              </div>
              <div className="col-8">
                <div className="d-flex align-items-center gap-3">
                  <span
                    className={`badge ${isActive ? 'bg-success' : isPaused ? 'bg-warning' : 'bg-danger'
                      }`}
                  >
                    {capitalize(status)}
                  </span>

                  {isActive && (
                    <Tooltip title="Pause Nitya Seva Donation" placement="top">
                      <button
                        onClick={() => handleNityaSevaPause('hold')}
                        className="btn btn-warning btn-sm badge"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Processing...' : 'Pause'}
                      </button>
                    </Tooltip>
                  )}

                  {isPaused && (
                    <Tooltip title="Resume Nitya Seva Donation" placement="top">
                      <button
                        onClick={() => handleNityaSevaPause('start')}
                        className="btn btn-success btn-sm badge"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Processing...' : 'Resume'}
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>

            {cancellation_reason && (
              <div className="row mb-3">
                <div className="col-4">
                  <strong>Cancellation Reason</strong>
                </div>
                <div className="col-8">{cancellation_reason}</div>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default NityasevaTableRow;